/**
 * Video on a softphone call: my camera on and off, and the other party's video (spec §5.2, §7.2, §10).
 *
 * Video always starts as an upgrade of an audio call that is already up. Turning the camera on adds (or reuses) a video
 * transceiver on the call's RTCPeerConnection and renegotiates; turning it off takes the track off again, goes
 * receive-only and renegotiates. The m-line is kept, so the next camera-on is only a direction change - and then the
 * camera goes onto the sender only after the answer, once the video's ICE path has settled (see icePath).
 *
 * The camera also goes off by itself:
 * - **on hold** (§10.1), with video inactive; unhold brings back what was there before the hold;
 * - **when the other party changes** (§10.2) - a privacy rule, the new party never gets my camera unasked;
 * - **when it fails:** the camera goes away (unplugged), the video transport fails ICE, the page unloads.
 *
 * SipVideo keeps the per-call state in the videoCalls store for the UI. It knows nothing about SipClient: SipClient
 * owns an instance, attaches each call's JsSIP session to it, hands it the remote 'track' events and tells it about hold
 * and a change of other party. Everything it needs from the outside - getUserMedia, the preferred camera,
 * renegotiation - comes in through SipVideoDeps.
 *
 * With a JsSIP older than 4.13.0 (see capabilities) my camera never comes on - the other party's video, when they
 * send it, is received and shown as with any JsSIP.
 */

import logger from '@/utilities/Logger'
import type { useVideoCallsStore, PeerVideo, RemoteCamera } from '@/stores/VideoCalls'
import { jssipSupportsVideo } from './capabilities'
import { orderVideoCodecs } from './codecPreferences'
import { candidatePairKey, iceUfrag, nextCandidatePair } from './icePath'
import { addLabelToMid } from './sdpLabel'
import { videoTransceiver } from './transceivers'

/** Bitrate cap towards a desktop peer */
export const DESKTOP_MAX_BITRATE = 1_500_000

/** How long a camera-on waits for the video to move to the new ICE path its answer started (0.3-0.6 s seen) */
export const NEW_PATH_TIMEOUT_MS = 3_000

/** Bitrate cap towards a mobile peer, and whenever the peer's class is not known */
export const MOBILE_MAX_BITRATE = 800_000

/** The preferred camera setting meaning "don't use a camera" ("Benyt ikke") */
const NO_CAMERA = 'NONE'

export interface SipVideoDeps {
	getUserMedia: (c: MediaStreamConstraints) => Promise<MediaStream>
	preferredCameraId: () => string | undefined // undefined or 'NONE' = no camera
	/**
	 * SipClient's renegotiation queue for the call: true = 2xx, false = refused (a 4xx such as 488, a 6xx, an answer that
	 * can't be applied) or the call ended; rejects for a passing failure (491, 5xx, nothing sent or answered)
	 */
	renegotiate: (session: VideoSession) => Promise<boolean>
	store: ReturnType<typeof useVideoCallsStore>
	/** The browser's video codecs for setCodecPreferences - defaults to RTCRtpReceiver.getCapabilities('video'); tests replace it, jsdom has no WebRTC */
	codecCapabilities?: () => RTCRtpCodec[]
}

export interface VideoSession { // the subset of JsSIP RTCSession used
	connection: RTCPeerConnection
	on(event: string, cb: (...a: unknown[]) => void): void
	off(event: string, cb: (...a: unknown[]) => void): void
}

/** Why the camera could not be opened */
export type CameraError = 'permission' | 'busy' | 'notfound'

/**
 * Map a getUserMedia failure to what the user is told
 * @param err The rejection from getUserMedia
 * @returns 'permission' when access was refused, 'notfound' when there is no such camera, otherwise 'busy' - which on
 * Windows usually means another app (often Teams) holds the camera
 */
export function cameraError(err: unknown): CameraError {
	switch ((err as { name?: unknown } | undefined)?.name) {
		case 'NotAllowedError':
		case 'SecurityError':
			return 'permission'
		case 'NotFoundError':
		case 'OverconstrainedError':
			return 'notfound'
		default:
			return 'busy'
	}
}

/** getUserMedia constraints for a camera: 1280x720 at 30 fps */
function cameraConstraints(deviceId: string): MediaStreamConstraints {
	return { video: { deviceId: { exact: deviceId }, width: 1280, height: 720, frameRate: 30 }, audio: false }
}

/** The video codecs this browser can receive, empty where there is no WebRTC */
function browserVideoCodecs(): RTCRtpCodec[] {
	if (typeof RTCRtpReceiver === 'undefined') { return [] }
	return RTCRtpReceiver.getCapabilities?.('video')?.codecs ?? []
}

function errorText(err: unknown): string {
	return err instanceof Error ? `${err.name}: ${err.message}` : String(err)
}

/** Whether a direction has this side sending */
function isSending(direction: RTCRtpTransceiverDirection | null | undefined): boolean {
	return direction === 'sendrecv' || direction === 'sendonly'
}

/**
 * Set a transceiver's direction
 * @returns false when it could not be set - a stopped transceiver or a closed connection throws InvalidStateError
 */
function setDirection(callId: string, transceiver: RTCRtpTransceiver, direction: RTCRtpTransceiverDirection): boolean {
	try {
		transceiver.direction = direction
		return true
	}
	catch (err) {
		logger.warning(`SipVideo: Could not set the video direction of call ${callId} to ${direction} (${errorText(err)})`)
		return false
	}
}

/** Drop a transceiver, so its m-line goes from the next offer - it may be stopped already, or the connection closed */
function stopTransceiver(callId: string, transceiver: RTCRtpTransceiver) {
	transceiver.sender.replaceTrack(null).catch(() => { /* stopped already - nothing is sent either way */ })

	try {
		transceiver.stop()
	}
	catch (err) {
		logger.warning(`SipVideo: Could not stop the video transceiver of call ${callId} (${errorText(err)})`)
	}
}

interface AttachedCall {
	session: VideoSession
	/** Removes the listeners attach() added */
	unlisten: () => void
	/**
	 * Bumped by every camera on/off, hold and detach. An operation that finds it changed after an await has been
	 * overtaken - by the user changing their mind or the call ending - and must not touch the call any more.
	 */
	intent: number
	/** Bumped by every camera switch: an older switch still opening its camera gives way to a newer one */
	switches: number
	/** How many renegotiations SipVideo has asked for on the call */
	renegotiations: number
	/** My camera, while it is open */
	localStream?: MediaStream
	/** Stops watching my camera track for the camera going away */
	unwatchLocal?: () => void
	/** Put on hold by me: onHold() took the camera off and made video inactive, onUnhold() undoes it */
	held: boolean
	/** The remote video track being followed, the transceiver it arrives on, and how to stop following it */
	remoteTrack?: MediaStreamTrack
	remoteTransceiver?: RTCRtpTransceiver
	unfollowRemote?: () => void
	/** Frames have arrived since the other party last started sending */
	remoteHadFrames: boolean
}

/** What turning the camera off did to the call's video transceiver */
interface CameraOff {
	transceiver?: RTCRtpTransceiver
	/** The sender may still hold the camera track */
	clearSender: boolean
	/** The direction changed, so the other party must be told */
	renegotiate: boolean
}

export class SipVideo {
	private readonly deps: SipVideoDeps

	private readonly calls = new Map<string, AttachedCall>()

	constructor(deps: SipVideoDeps) {
		this.deps = deps
	}

	/**
	 * Start handling video for a call, once its peer connection exists: create its video state, label the camera in
	 * every SDP it sends, and watch for its video transport failing and for completed negotiations
	 * @param callId The call id
	 * @param session The call's JsSIP session
	 */
	attach(callId: string, session: VideoSession): void {
		const existing = this.calls.get(callId)

		if (existing?.session === session) { return }
		if (existing) { this.detach(callId) }

		const pc = session.connection as RTCPeerConnection | undefined
		const onSdp = (...args: unknown[]) => { this.labelCamera(session, args[0]) }
		const onMediaFailed = (...args: unknown[]) => { this.onMediaFailed(callId, args[0]) }
		// An offer/answer completed - ours, or the other party's re-INVITE: what we receive may have changed
		const onSignalingState = () => { if (pc?.signalingState === 'stable') { this.refreshRemote(callId) } }

		session.on('sdp', onSdp)
		session.on('mediafailed', onMediaFailed)
		pc?.addEventListener('signalingstatechange', onSignalingState)

		const unlisten = () => {
			session.off('sdp', onSdp)
			session.off('mediafailed', onMediaFailed)
			pc?.removeEventListener('signalingstatechange', onSignalingState)
		}

		this.calls.set(callId, { session, unlisten, intent: 0, switches: 0, renegotiations: 0, held: false, remoteHadFrames: false })
		this.deps.store.ensure(callId)
	}

	/**
	 * Stop handling video for a call, when it has ended: stop my camera, stop following remote video, remove the
	 * session listeners and the call's video state
	 * @param callId The call id
	 */
	detach(callId: string): void {
		const call = this.calls.get(callId)

		if (call) {
			call.intent++
			this.calls.delete(callId)
			call.unlisten()
			this.stopLocal(call)
			call.unfollowRemote?.()
		}
		this.deps.store.remove(callId)
	}

	/**
	 * Record the class of the other party's device, as published by the PBX. Undefined means the peer can't do video.
	 * @param callId The call id
	 * @param peer 'desktop' or 'mobile', or undefined
	 */
	setPeerVideo(callId: string, peer?: PeerVideo): void {
		if (!this.calls.has(callId)) { return }
		this.deps.store.patch(callId, { peerVideo: peer })
	}

	/**
	 * Turn my camera on: open it, send it on the call's video transceiver and renegotiate. Does nothing when the
	 * installed JsSIP can't carry video, no camera is chosen, the camera is already on or starting, the call is on hold,
	 * or video was rejected earlier in the call.
	 * @param callId The call id
	 */
	async enableCamera(callId: string): Promise<void> {
		// The UI offers no camera then - this is the backstop: with an older JsSIP a failed camera re-INVITE ends the call
		if (!jssipSupportsVideo()) {
			logger.info(`SipVideo: The installed JsSIP can not carry my camera - not turning it on for call ${callId}`)
			return
		}

		const call = this.calls.get(callId)

		if (!call) {
			logger.warning(`SipVideo: Can not turn the camera on for call ${callId}, the call is not attached`)
			return
		}

		const deviceId = this.deps.preferredCameraId()

		if (!deviceId || deviceId === NO_CAMERA) {
			logger.info(`SipVideo: No camera chosen - not turning the camera on for call ${callId}`)
			return
		}

		const state = this.deps.store.ensure(callId)

		if (state.unavailable) {
			logger.info(`SipVideo: Video was rejected earlier on call ${callId} - not turning the camera on`)
			return
		}
		if (state.local === 'on' || state.local === 'starting') { return }
		if (call.held) {
			logger.info(`SipVideo: Call ${callId} is on hold - not turning the camera on`)
			return
		}

		const ticket = ++call.intent
		const isCurrent = () => this.calls.get(callId) === call && call.intent === ticket

		this.deps.store.patch(callId, { local: 'starting', error: undefined })

		let stream: MediaStream

		try {
			stream = await this.deps.getUserMedia(cameraConstraints(deviceId))
		}
		catch (err) {
			if (!isCurrent()) { return }

			const error = cameraError(err)

			logger.warning(`SipVideo: Could not open the camera for call ${callId} (${errorText(err)}) - reporting it as ${error}`)
			this.deps.store.patch(callId, { local: 'error', error })
			return
		}

		const track = stream.getVideoTracks()[0]

		if (!isCurrent() || !track) {
			stream.getTracks().forEach(each => { each.stop() })
			if (isCurrent()) {
				logger.warning(`SipVideo: The camera for call ${callId} opened without a video track`)
				this.deps.store.patch(callId, { local: 'error', error: 'notfound' })
			}
			return
		}

		// From here on whoever overtakes us - disableCamera, onHold or detach - finds the camera here and stops it
		call.localStream = stream
		this.watchLocal(callId, call, track)

		const pc = call.session.connection
		const existing = videoTransceiver(pc)
		const previousDirection = existing?.direction ?? 'recvonly'
		// Over a transceiver that is already negotiated the offer carries the direction change alone, and the camera goes
		// on once the answer is in and the video path has settled (see icePath). A new transceiver gets a new transport,
		// and Chrome holds what it sends until that transport works.
		const attachAfterAnswer = existing !== undefined && existing.mid !== null
		let transceiver: RTCRtpTransceiver

		try {
			if (existing) {
				if (!attachAfterAnswer) {
					await existing.sender.replaceTrack(track)
					if (!isCurrent()) { return }
				}
				existing.direction = 'sendrecv'
				transceiver = existing
			} else {
				transceiver = pc.addTransceiver(track, { direction: 'sendrecv' })
			}
		}
		catch (err) {
			if (!isCurrent()) { return }
			logger.warning(`SipVideo: Could not add the camera to call ${callId} (${errorText(err)})`)
			this.stopLocal(call)
			this.deps.store.patch(callId, { local: 'error', error: 'media' })
			return
		}

		this.applyCodecOrder(callId, transceiver)
		await this.capBitrate(callId, transceiver)
		if (!isCurrent()) { return }

		// What the video path is before the offer, to tell afterwards whether the answer moved it
		const ufragBefore = attachAfterAnswer ? iceUfrag(pc.remoteDescription?.sdp, transceiver.mid) : undefined
		const pairBefore = attachAfterAnswer ? candidatePairKey(transceiver.sender.transport?.iceTransport) : undefined
		let accepted: boolean

		try {
			accepted = await this.renegotiate(call)
		}
		catch (err) {
			if (!isCurrent()) { return }
			// A passing failure - glare that outlasted JsSIP's retry (491), a server error (5xx), or nothing sent or answered.
			// It says nothing about whether video works on the call, so the camera can be tried again.
			logger.warning(`SipVideo: Could not renegotiate the camera onto call ${callId} (${errorText(err)}) - the camera stays off`)
			this.undoUpgrade(callId, call, transceiver, existing ? previousDirection : undefined)
			this.deps.store.patch(callId, { local: 'off', localStream: undefined, error: 'media' })
			return
		}

		if (!isCurrent()) { return }

		// A 2xx can still refuse my video, e.g. by answering the video m-line with port 0
		if (accepted && !isSending(transceiver.currentDirection)) {
			logger.warning(`SipVideo: The answer on call ${callId} does not let me send video (${transceiver.currentDirection}) - taking it as a rejection`)
			accepted = false
		}

		if (!accepted) {
			logger.warning(`SipVideo: The video upgrade was rejected on call ${callId} - video is not available on this call`)
			this.undoUpgrade(callId, call, transceiver, existing ? previousDirection : undefined)
			this.deps.store.patch(callId, { local: 'off', localStream: undefined, unavailable: true, error: 'rejected' })
			return
		}

		if (attachAfterAnswer) {
			await this.awaitVideoPath(callId, pc, transceiver, ufragBefore, pairBefore)
			if (!isCurrent()) { return }

			try {
				await transceiver.sender.replaceTrack(track)
			}
			catch (err) {
				if (!isCurrent()) { return }
				logger.warning(`SipVideo: Could not put the camera on call ${callId} after the answer (${errorText(err)}) - turning it off again`)
				await this.disableCamera(callId)
				this.deps.store.patch(callId, { error: 'media' })
				return
			}
			if (!isCurrent()) { return }
		}

		this.deps.store.patch(callId, { local: 'on', localStream: stream })
		logger.info(`SipVideo: Camera is on for call ${callId}`)
	}

	/**
	 * After a camera-on answer: when it restarted ICE on the video transport, wait - at most NEW_PATH_TIMEOUT_MS - for the
	 * video to move to its new candidate pair, so the camera's first frames and Chrome's bandwidth probe go out on it
	 * (see icePath). An answer that leaves ICE alone returns at once.
	 */
	private async awaitVideoPath(callId: string, pc: RTCPeerConnection, transceiver: RTCRtpTransceiver, ufragBefore: string | undefined, pairBefore: string | undefined) {
		const ufragAfter = iceUfrag(pc.remoteDescription?.sdp, transceiver.mid)

		if (ufragBefore === undefined || ufragAfter === ufragBefore) { return }

		const ice = transceiver.sender.transport?.iceTransport

		// It moved before we got here
		if (candidatePairKey(ice) !== pairBefore) { return }

		logger.info(`SipVideo: The camera-on answer restarted ICE on the video of call ${callId} - waiting for the new path before sending`)
		if (!await nextCandidatePair(ice, NEW_PATH_TIMEOUT_MS)) {
			logger.warning(`SipVideo: The video of call ${callId} is still on its old path after ${NEW_PATH_TIMEOUT_MS} ms - sending the camera anyway`)
		}
	}

	/**
	 * Turn my camera off: take the track off the sender, stop it, go receive-only and renegotiate. The m-line stays, so
	 * the other party's video keeps coming and the camera can come back with just a direction change.
	 * @param callId The call id
	 */
	async disableCamera(callId: string): Promise<void> {
		const call = this.calls.get(callId)

		if (!call) { return }

		// Overtakes an enableCamera still waiting for the camera or the renegotiation
		call.intent++

		const off = this.takeCameraOff(callId, call, 'recvonly')

		this.deps.store.patch(callId, { local: 'off', localStream: undefined, error: undefined })

		if (!off.transceiver || !off.clearSender) { return }

		await off.transceiver.sender.replaceTrack(null).catch((err: unknown) => {
			logger.warning(`SipVideo: Could not take the camera off the sender on call ${callId} (${errorText(err)})`)
		})

		// Only the direction is in the SDP - without a change there is nothing to renegotiate, and an ended call has no one to tell
		if (!off.renegotiate || this.calls.get(callId) !== call) { return }

		const accepted = await this.renegotiate(call).catch(() => false)

		if (accepted) {
			logger.info(`SipVideo: Camera is off for call ${callId}`)
		} else {
			logger.warning(`SipVideo: Camera is off for call ${callId}, but the renegotiation failed - the other party may still show my last frame`)
		}
	}

	/**
	 * Switch my camera to another device during the call: the new camera goes onto the sender with replaceTrack, no
	 * renegotiation. Only while the camera is on - otherwise the next camera-on opens the preferred camera anyway.
	 * @param callId The call id
	 * @param deviceId The camera to use - 'NONE' turns the camera off
	 */
	async switchCamera(callId: string, deviceId: string): Promise<void> {
		const call = this.calls.get(callId)

		if (!call) { return }
		if (!deviceId || deviceId === NO_CAMERA) {
			await this.disableCamera(callId)
			return
		}
		if (this.deps.store.calls[callId]?.local !== 'on') { return }

		const ticket = call.intent
		const switchTicket = ++call.switches
		const isCurrent = () => this.calls.get(callId) === call && call.intent === ticket && call.switches === switchTicket

		let stream: MediaStream

		try {
			stream = await this.deps.getUserMedia(cameraConstraints(deviceId))
		}
		catch (err) {
			if (!isCurrent()) { return }

			const error = cameraError(err)

			logger.warning(`SipVideo: Could not open camera ${deviceId} for call ${callId} (${errorText(err)}) - keeping the current camera`)
			this.deps.store.patch(callId, { error })
			return
		}

		const track = stream.getVideoTracks()[0]
		const transceiver = videoTransceiver(call.session.connection)
		const discard = () => { stream.getTracks().forEach(each => { each.stop() }) }

		if (!isCurrent() || !track || !transceiver) {
			discard()
			return
		}

		try {
			await transceiver.sender.replaceTrack(track)
		}
		catch (err) {
			discard()
			if (isCurrent()) { logger.warning(`SipVideo: Could not switch the camera of call ${callId} (${errorText(err)}) - keeping the current camera`) }
			return
		}

		// Turned off, held or switched again meanwhile - whoever did that takes care of the sender
		if (!isCurrent()) {
			discard()
			return
		}

		this.stopLocal(call)
		call.localStream = stream
		this.watchLocal(callId, call, track)
		this.deps.store.patch(callId, { localStream: stream, error: undefined })
		logger.info(`SipVideo: Switched the camera of call ${callId} to ${track.label || deviceId}`)
	}

	/**
	 * The call is being put on hold by me (spec §10.1) - called before the hold re-INVITE, which then carries the change:
	 * my camera stops and video goes inactive. Whether the camera was on is remembered, for onUnhold.
	 * @param callId The call id
	 */
	onHold(callId: string): void {
		const call = this.calls.get(callId)

		if (!call || call.held) { return }

		const local = this.deps.store.ensure(callId).local
		const resumeCamera = local === 'on' || local === 'starting'

		call.intent++
		call.held = true

		const off = this.takeCameraOff(callId, call, 'inactive')

		this.deps.store.patch(callId, { local: 'off', localStream: undefined, resumeCamera: resumeCamera ? true : undefined })
		if (off.clearSender) {
			off.transceiver?.sender.replaceTrack(null).catch(() => { /* the camera is stopped anyway */ })
		}
		logger.info(`SipVideo: Call ${callId} going on hold - camera off${resumeCamera ? ', it comes back at unhold' : ''}`)
	}

	/**
	 * The call is no longer held by me - after the unhold re-INVITE, or when the hold failed and JsSIP took it back:
	 * receive video again, and turn my camera back on if it was on before the hold
	 * @param callId The call id
	 */
	onUnhold(callId: string): void {
		const call = this.calls.get(callId)

		if (!call?.held) { return }
		call.held = false

		const resumeCamera = this.deps.store.calls[callId]?.resumeCamera === true
		const transceiver = videoTransceiver(call.session.connection)
		const restored = transceiver?.direction === 'inactive' && setDirection(callId, transceiver, 'recvonly')

		this.deps.store.patch(callId, { resumeCamera: undefined })

		if (!resumeCamera) {
			if (restored) { void this.renegotiateQuietly(callId, call, 'receiving video again after hold') }
			return
		}

		// The camera's own renegotiation carries the restored direction too - unless it never gets that far
		const before = call.renegotiations

		logger.info(`SipVideo: Call ${callId} is off hold - turning the camera back on`)
		void this.enableCamera(callId).then(() => {
			if (restored && this.calls.get(callId) === call && call.renegotiations === before) {
				void this.renegotiateQuietly(callId, call, 'receiving video again after hold')
			}
		})
	}

	/**
	 * The other party of the call changed - transfer, pickup, a replaced leg (spec §10.2): my camera goes off, so the
	 * new party never gets it unasked, and the old party's device class no longer applies
	 * @param callId The call id
	 */
	onPeerChanged(callId: string): void {
		if (!this.calls.has(callId)) { return }

		logger.info(`SipVideo: The other party of call ${callId} changed - turning the camera off`)
		this.deps.store.patch(callId, { peerVideo: undefined, resumeCamera: undefined })
		void this.disableCamera(callId)
	}

	/** Turn my camera off on every call - the page is unloading */
	stopAllCameras(): void {
		for (const callId of [...this.calls.keys()]) {
			const local = this.deps.store.calls[callId]?.local

			if (local === 'on' || local === 'starting') { void this.disableCamera(callId) }
		}
	}

	/**
	 * Follow the other party's video: a remote 'track' event from the call's peer connection. Audio tracks are ignored.
	 * Whether frames are arriving comes from the track's mute/unmute events, which follow the RTP flow; whether the other
	 * party sends at all comes from the negotiated direction.
	 * @param callId The call id
	 * @param event The track event
	 */
	onTrack(callId: string, event: RTCTrackEvent): void {
		const track = event.track

		if (track.kind !== 'video') { return }

		const call = this.calls.get(callId)

		if (!call) {
			logger.warning(`SipVideo: Remote video for call ${callId}, which is not attached - ignoring it`)
			return
		}

		// The browser fires 'track' again for the same track when the direction goes back to receiving
		if (call.remoteTrack === track) {
			this.refreshRemote(callId)
			return
		}
		call.unfollowRemote?.()

		const store = this.deps.store
		const onMuteChange = () => { this.refreshRemote(callId) }

		function onEnded() {
			unfollow()
			store.patch(callId, { remote: 'none', remoteStream: undefined, remoteAspect: undefined })
		}

		function unfollow() {
			track.removeEventListener('mute', onMuteChange)
			track.removeEventListener('unmute', onMuteChange)
			track.removeEventListener('ended', onEnded)
			if (call?.remoteTrack === track) {
				call.remoteTrack = undefined
				call.remoteTransceiver = undefined
				call.unfollowRemote = undefined
			}
		}

		track.addEventListener('mute', onMuteChange)
		track.addEventListener('unmute', onMuteChange)
		track.addEventListener('ended', onEnded)
		call.remoteTrack = track
		// Test doubles and older browsers may leave the event's transceiver out
		call.remoteTransceiver = (event.transceiver as RTCRtpTransceiver | undefined) ?? call.session.connection.getTransceivers().find(each => each.receiver.track === track)
		call.remoteHadFrames = false
		call.unfollowRemote = unfollow

		// A stream of its own with only the video track: the call's audio already plays through the <audio> element, so a
		// shared stream in the <video> element would play it twice
		store.patch(callId, { remoteStream: new MediaStream([track]) })
		this.refreshRemote(callId)
	}

	/**
	 * Work out the other party's video state:
	 * - 'none' when the negotiation says they don't send - their camera is off - or no frames have come yet;
	 * - 'paused' when they should be sending but the frames stopped, e.g. a third member joined the bridge (§10.3);
	 * - 'live' while frames arrive.
	 */
	private refreshRemote(callId: string) {
		const call = this.calls.get(callId)
		const track = call?.remoteTrack

		if (!call || !track) { return }

		const negotiated = call.remoteTransceiver?.currentDirection
		let remote: RemoteCamera

		if (negotiated === 'sendonly' || negotiated === 'inactive' || negotiated === 'stopped') {
			call.remoteHadFrames = false
			remote = 'none'
		} else if (!track.muted) {
			call.remoteHadFrames = true
			remote = 'live'
		} else {
			remote = call.remoteHadFrames ? 'paused' : 'none'
		}

		if (this.deps.store.calls[callId]?.remote !== remote) { this.deps.store.patch(callId, { remote }) }
	}

	/**
	 * Stop my camera and take it off the video transceiver, without renegotiating
	 * @param direction 'recvonly' to keep receiving - only changed if the transceiver was sending - or 'inactive' for hold
	 */
	private takeCameraOff(callId: string, call: AttachedCall, direction: 'recvonly' | 'inactive'): CameraOff {
		const hadCamera = call.localStream !== undefined

		this.stopLocal(call)

		const transceiver = videoTransceiver(call.session.connection)

		if (!transceiver) { return { clearSender: false, renegotiate: false } }

		// Added by a camera-on that never got as far as offering it: drop it rather than offer video nobody wants
		if (transceiver.mid === null) {
			stopTransceiver(callId, transceiver)
			return { clearSender: false, renegotiate: false }
		}

		const current = transceiver.direction
		const wanted = (direction === 'inactive' || isSending(current)) ? direction : current
		const changed = wanted !== current && setDirection(callId, transceiver, wanted)

		return { transceiver, clearSender: hadCamera || isSending(current), renegotiate: changed }
	}

	/**
	 * Take back a camera-on that didn't work out: stop the camera, drop a transceiver we added - so a later refresh
	 * doesn't offer the refused m-line again - or put back the one that was there already. Never throws: the answer may
	 * have stopped the transceiver already.
	 * @param restoreDirection The direction of a transceiver that was there before, undefined for one we added
	 */
	private undoUpgrade(callId: string, call: AttachedCall, transceiver: RTCRtpTransceiver, restoreDirection?: RTCRtpTransceiverDirection) {
		this.stopLocal(call)

		if (restoreDirection === undefined) {
			stopTransceiver(callId, transceiver)
			return
		}
		if (transceiver.direction !== 'stopped') { setDirection(callId, transceiver, restoreDirection) }
		transceiver.sender.replaceTrack(null).catch(() => { /* the track is stopped anyway */ })
	}

	/** Turn the camera off when it goes away during the call, e.g. unplugged (spec §10.4) */
	private watchLocal(callId: string, call: AttachedCall, track: MediaStreamTrack) {
		const onEnded = () => {
			logger.warning(`SipVideo: The camera of call ${callId} went away - turning the camera off`)
			void this.disableCamera(callId)
		}

		call.unwatchLocal?.()
		track.addEventListener('ended', onEnded)
		call.unwatchLocal = () => { track.removeEventListener('ended', onEnded) }
	}

	private renegotiate(call: AttachedCall): Promise<boolean> {
		call.renegotiations++
		return this.deps.renegotiate(call.session)
	}

	private async renegotiateQuietly(callId: string, call: AttachedCall, why: string) {
		const accepted = await this.renegotiate(call).catch(() => false)

		if (!accepted) { logger.warning(`SipVideo: Renegotiating for ${why} failed on call ${callId}`) }
	}

	/** The fork reports ICE failing on the video transport without ending the call: drop my video, keep the call (spec §10.4) */
	private onMediaFailed(callId: string, event: unknown) {
		const failed = event as { kind?: unknown, mid?: unknown } | undefined

		if (failed?.kind !== 'video' || !this.calls.has(callId)) { return }

		logger.warning(`SipVideo: The video transport of call ${callId} failed (mid ${String(failed.mid)}) - turning the camera off, the call goes on`)
		void this.disableCamera(callId)
		this.deps.store.patch(callId, { error: 'media' })
	}

	/** Label the video m-section of an outgoing SDP as the camera */
	private labelCamera(session: VideoSession, event: unknown) {
		const sdpEvent = event as { originator?: unknown, sdp?: unknown } | undefined

		if (sdpEvent?.originator !== 'local' || typeof sdpEvent.sdp !== 'string') { return }

		// JsSIP has no connection until the call's peer connection is created
		const mid = session.connection ? videoTransceiver(session.connection)?.mid : undefined

		if (mid) { sdpEvent.sdp = addLabelToMid(sdpEvent.sdp, mid, 'camera') }
	}

	/** Put the codecs in our order (spec §5.5) - failing that the browser's default order still works, if less well */
	private applyCodecOrder(callId: string, transceiver: RTCRtpTransceiver) {
		const codecs = orderVideoCodecs((this.deps.codecCapabilities ?? browserVideoCodecs)())

		if (codecs.length === 0) {
			logger.warning(`SipVideo: Neither H.264 nor VP8 is available - leaving the codec order of call ${callId} to the browser`)
			return
		}

		try {
			transceiver.setCodecPreferences(codecs)
		}
		catch (err) {
			logger.warning(`SipVideo: Could not set the codec order for call ${callId} (${errorText(err)})`)
		}
	}

	/** Cap the camera's bitrate by the other party's device class - best effort, the call works without it */
	private async capBitrate(callId: string, transceiver: RTCRtpTransceiver) {
		const maxBitrate = this.deps.store.calls[callId]?.peerVideo === 'desktop' ? DESKTOP_MAX_BITRATE : MOBILE_MAX_BITRATE

		try {
			const parameters = transceiver.sender.getParameters()
			const encoding = parameters.encodings[0]

			if (!encoding) {
				logger.warning(`SipVideo: The camera sender of call ${callId} has no encodings - bitrate not capped`)
				return
			}
			encoding.maxBitrate = maxBitrate
			await transceiver.sender.setParameters(parameters)
		}
		catch (err) {
			logger.warning(`SipVideo: Could not cap the camera bitrate for call ${callId} (${errorText(err)})`)
		}
	}

	/** Stop my camera on a call, if it is open */
	private stopLocal(call: AttachedCall) {
		call.unwatchLocal?.()
		call.unwatchLocal = undefined
		call.localStream?.getTracks().forEach(track => { track.stop() })
		call.localStream = undefined
	}
}
